import Loading from "components/center-loading";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  searchBusinessUnit,
  searchFamily,
  searchMaterialGroup,
  searchSuppliers,
} from "services/filters.service";
import type { IOption } from "types/option";
import { isUseFilterDefault } from "utils/user";
import type {
  FiltersLoadCapacityType,
  FiltersUnloadCapacityType,
  GridParameterizationContextProps,
  GridParameterizationProviderProps,
  ICalendarDateView,
} from "./types";
import {
  filtersLoadCapacityDefault,
  filtersUnloadCapacityDefault,
  gridParameterizationType,
  unloadCapacityRadioValues,
} from "./utils";

const GridParameterizationContext = createContext<GridParameterizationContextProps | undefined>(undefined);

const GridParameterizationProvider = ({ children }: GridParameterizationProviderProps) => {
  const [loading, setLoading] = useState(0);
  const [businessUnits, setBusinessUnits] = useState<IOption[] | []>([]);
  const [suppliers, setSuppliers] = useState<IOption[] | []>([]);
  const [materialGroups, setMaterialGroups] = useState<IOption[] | []>([]);
  const [families, setFamilies] = useState<IOption[] | []>([]);
  const [filtersLoadCapacity, setFiltersLoadCapacity] = useState<FiltersLoadCapacityType>(
    filtersLoadCapacityDefault
  );
  const [selectedRadioUnloadCapacity, setSelectedRadioUnloadCapacity] = useState(unloadCapacityRadioValues.materialGroup);
  const [filtersUnloadCapacity, setFiltersUnloadCapacity] = useState<FiltersUnloadCapacityType>(
    filtersUnloadCapacityDefault
  );
  const [calendarDateView, setCalendarDateView] = useState<ICalendarDateView>({
    startDate: undefined,
    endDate: undefined,
  });

  const startLoading = useCallback(() => setLoading((prevState) => prevState + 1), []);
  const stopLoading = useCallback(() => setLoading((prevState) => prevState - 1), []);

  const loadBusinessUnits = useCallback(async () => {
    try {
      startLoading();
      const result = await searchBusinessUnit(isUseFilterDefault());
      setBusinessUnits(result);
    } catch (error) {
      console.error(error);
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  const loadSuppliers = useCallback(async () => {
    try {
      startLoading();
      const query = `BusinessUnitId=${filtersLoadCapacity.businessUnitId}`;
      const result = await searchSuppliers(isUseFilterDefault(), query);

      const uniqueSuppliers = result.filter(
        (item: IOption, index: number, self: IOption[]) =>
          index === self.findIndex((t) => t.Id === item.Id)
      );

      setSuppliers(uniqueSuppliers);
    } catch (error) {
      console.error(error);
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading, filtersLoadCapacity.businessUnitId]);

  const loadMaterialGroups = useCallback(async () => {
    try {
      startLoading();
      const query = `BusinessUnitId=${filtersUnloadCapacity.businessUnitId}`;
      const result = await searchMaterialGroup(isUseFilterDefault(), query);
      setMaterialGroups(result);
    } catch (error) {
      console.error(error);
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading, filtersUnloadCapacity.businessUnitId]);

  const loadFamilies = useCallback(async () => {
    try {
      startLoading();
      const query = `BusinessUnitId=${filtersUnloadCapacity.businessUnitId}`;
      const result = await searchFamily(isUseFilterDefault(), query);
      const _result = result.map((x: { Name: string }) => ({ ...x, Description: x.Name }));
      setFamilies(_result);
    } catch (error) {
      console.error(error);
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading, filtersUnloadCapacity.businessUnitId]);

  const handleChangeFilter = useCallback((type: string, prop: string, value: number | null) => {
    if (type === gridParameterizationType.UnloadCapacity && prop === "businessUnitId") {
      setFiltersUnloadCapacity({
        ...filtersUnloadCapacityDefault,
        [prop]: value ?? undefined,
      });
      setMaterialGroups([]);
      setFamilies([]);
    } else if (type === gridParameterizationType.UnloadCapacity && prop !== "businessUnitId") {
      setFiltersUnloadCapacity((prevState) => ({
        ...prevState,
        [prop]: value,
      }));
    } else if (type === gridParameterizationType.LoadCapacity && prop === "businessUnitId") {
      setFiltersLoadCapacity({
        ...filtersLoadCapacityDefault,
        [prop]: value ?? undefined,
      });
      setSuppliers([]);
    } else if (type === gridParameterizationType.LoadCapacity && prop !== "businessUnitId") {
      setFiltersLoadCapacity((prevState) => ({
        ...prevState,
        [prop]: value,
      }));
    }
  }, []);

  useEffect(() => {
    loadBusinessUnits();
  }, [loadBusinessUnits]);

  useEffect(() => {
    if (filtersLoadCapacity.businessUnitId) loadSuppliers();
  }, [filtersLoadCapacity.businessUnitId, loadSuppliers]);

  useEffect(() => {
    if (filtersUnloadCapacity.businessUnitId) {
      loadMaterialGroups();
      loadFamilies();
    }
  }, [filtersUnloadCapacity.businessUnitId, loadMaterialGroups, loadFamilies]);

  const values = useMemo(
    () => ({
      startLoading,
      stopLoading,
      businessUnits,
      suppliers,
      materialGroups,
      families,
      filtersLoadCapacity,
      filtersUnloadCapacity,
      handleChangeFilter,
      selectedRadioUnloadCapacity,
      setSelectedRadioUnloadCapacity,
      calendarDateView,
      setCalendarDateView,
    }),
    [
      startLoading,
      stopLoading,
      businessUnits,
      suppliers,
      materialGroups,
      families,
      filtersLoadCapacity,
      filtersUnloadCapacity,
      handleChangeFilter,
      selectedRadioUnloadCapacity,
      calendarDateView,
    ]
  );

  return(
    <GridParameterizationContext.Provider value={values}>
      {children}
      <Loading isLoading={loading > 0} />
    </GridParameterizationContext.Provider>
  )
};

const useGridParameterizationContext = () => {
  const context = useContext(GridParameterizationContext);

  if (!context) {
    throw new Error("useGridParameterizationContext must be used within a GridParameterizationProvider");
  }

  return context;
};

export {
  GridParameterizationContext,
  GridParameterizationProvider,
  useGridParameterizationContext
};
